(() => {
  class ProductInformation extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-product-json]')?.textContent || '[]');
      this.form = this.querySelector('[data-product-form]');
      this.addButton = this.querySelector('[data-add-to-cart]');
      this.optionFields = [...this.querySelectorAll('[data-option-position]')];
      this.optionFields.forEach((field) => {
        field.addEventListener('click', (event) => {
          const button = event.target.closest('[data-option-value]');
          if (!button) return;
          this.selectButton(field, button);
          this.onOptionsChanged();
        });
        field.querySelector('[data-option-select]')?.addEventListener('change', () => this.onOptionsChanged());
      });
      this.setupGalleryCounter();
      this.setupStickyButton();
    }

    selectButton(field, selectedButton) {
      field.querySelectorAll('[data-option-value]').forEach((button) => {
        const selected = button === selectedButton;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      const label = field.querySelector('[data-option-label]');
      if (label) label.textContent = selectedButton.dataset.optionValue;
    }

    getSelectedOptions() {
      return this.optionFields.map((field) => {
        const button = field.querySelector('[data-option-value].is-selected');
        const select = field.querySelector('[data-option-select]');
        return button?.dataset.optionValue || select?.value || '';
      });
    }

    onOptionsChanged() {
      const options = this.getSelectedOptions();
      const variant = this.variants.find((item) => item.options.every((value, index) => value === options[index]));
      this.updateAvailability(options);
      this.updateVariant(variant);
    }

    updateAvailability(selectedOptions) {
      this.optionFields.forEach((field, fieldIndex) => {
        field.querySelectorAll('[data-option-value]').forEach((button) => {
          const candidate = [...selectedOptions];
          candidate[fieldIndex] = button.dataset.optionValue;
          const available = this.variants.some((variant) => variant.available && variant.options.every((value, index) => value === candidate[index]));
          button.toggleAttribute('data-unavailable', !available);
        });
      });
    }

    updateVariant(variant) {
      const variantInput = this.querySelector('[data-variant-id]');
      const price = this.querySelector('[data-price]');
      const comparePrice = this.querySelector('[data-compare-price]');
      const saving = this.querySelector('[data-saving]');
      const submitText = this.querySelector('[data-add-to-cart-text]');
      const submitPrice = this.querySelector('[data-add-to-cart-price]');
      const stickyVariant = this.querySelector('[data-sticky-variant]');
      const stickyPrice = this.querySelector('[data-sticky-price]');

      if (!variant) {
        variantInput.value = '';
        this.addButton.disabled = true;
        submitText.textContent = window.theme.strings.unavailable;
        submitPrice.textContent = '';
        return;
      }

      variantInput.value = variant.id;
      price.textContent = this.formatMoney(variant.price);
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        comparePrice.textContent = this.formatMoney(variant.compare_at_price);
        comparePrice.hidden = false;
        if (saving) { saving.textContent = `Save ${this.formatMoney(variant.compare_at_price - variant.price)}`; saving.hidden = false; }
      } else {
        comparePrice.textContent = '';
        comparePrice.hidden = true;
        if (saving) { saving.textContent = ''; saving.hidden = true; }
      }
      this.addButton.disabled = !variant.available;
      submitText.textContent = variant.available ? window.theme.strings.addToCart : window.theme.strings.soldOut;
      submitPrice.textContent = variant.available ? `· ${this.formatMoney(variant.price)}` : '';
      if (stickyVariant) stickyVariant.textContent = variant.title;
      if (stickyPrice) stickyPrice.textContent = this.formatMoney(variant.price);
      const inventory = this.querySelector('[data-inventory-message]');
      if (inventory) {
        const threshold = Number(inventory.dataset.lowStockThreshold || 0);
        inventory.textContent = variant.available && variant.inventory_management && variant.inventory_quantity > 0 && variant.inventory_quantity <= threshold ? `Low stock · ${variant.inventory_quantity} remaining` : '';
      }

      history.replaceState({}, '', `${this.dataset.productUrl}?variant=${variant.id}`);
      if (variant.featured_media?.id) {
        const media = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
        media?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
      }
    }

    formatMoney(cents) {
      if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
      const currency = document.documentElement.lang === 'en-GB' ? 'GBP' : (window.Shopify?.currency?.active || 'GBP');
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency }).format(cents / 100);
    }

    setupGalleryCounter() {
      const gallery = this.querySelector('[data-product-gallery]');
      const current = this.querySelector('[data-gallery-current]');
      if (!gallery || !current) return;
      let timer;
      gallery.addEventListener('scroll', () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => {
          const items = [...gallery.querySelectorAll('[data-media-position]')];
          const center = gallery.scrollLeft + gallery.clientWidth / 2;
          const active = items.reduce((closest, item) => Math.abs(item.offsetLeft + item.offsetWidth / 2 - center) < Math.abs(closest.offsetLeft + closest.offsetWidth / 2 - center) ? item : closest, items[0]);
          current.textContent = active?.dataset.mediaPosition || '1';
        }, 60);
      }, { passive: true });
    }

    setupStickyButton() {
      const sticky = this.querySelector('[data-sticky-atc]');
      const stickyButton = this.querySelector('[data-sticky-atc-button]');
      if (!sticky || !stickyButton || !this.addButton) return;
      let hasBeenVisible = false;
      new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) { hasBeenVisible = true; sticky.hidden = true; }
        else if (hasBeenVisible) sticky.hidden = false;
      }, { threshold: 0 }).observe(this.addButton);
      stickyButton.addEventListener('click', () => this.form.requestSubmit(this.addButton));
    }
  }

  if (!customElements.get('product-information')) customElements.define('product-information', ProductInformation);
})();
